/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    createActivityCancellationPolicies,
    deleteActivityCancellationPolicies,
    getActivityCancellationPolicies,
    updateActivityCancellationPolicies,
} from "../activityService";
import Toster from "../../../../common/Toster";
import {
    abAddBtn,
    abDeleteBtn,
    abEmptyState,
    abListFieldInput,
} from "../activityBuilderUi";

const DeleteConfirmationModal = ({ isOpen, onCancel, onConfirm, title, message }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-200">
                <div className="p-6">
                    <div className="flex items-center space-x-3 text-red-600 mb-4">
                        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                            <i className="fi fi-rr-triangle-warning text-xl"></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
                    </div>
                    <p className="text-gray-500 text-sm font-medium leading-relaxed">
                        {message}
                    </p>
                </div>
                <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
                    <button
                        onClick={onCancel}
                        className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-white transition-all active:scale-95"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-8 py-2.5 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary/90 transition-all shadow-lg shadow-primary/20 active:scale-95"
                    >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

const preserveWindowScroll = (action) => {
    const scrollY = window.scrollY;
    action();
    window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
            window.scrollTo({ top: scrollY, left: 0 });
        });
    });
};

const ActivityCancellation = ({
    activityId,
    policies,
    setPolicies,
    registerSave,
    onValidityChange,
}) => {
    const queryClient = useQueryClient();
    const [text, setText] = useState("");
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, index: null });
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [toastMsg, setToastMsg] = useState("");

    /** Avoid wiping local rows when the API still returns [] on background refetch (React Query new references). */
    const hasHydratedCancellationFromApi = useRef(false);

    const storageKey = useMemo(() => {
        return `activity_cancellation_policies_${activityId || "new"}`;
    }, [activityId]);

    const { data: cancellationData, isLoading: isFetching } = useQuery({
        queryKey: ["activityCancellationPolicies", activityId],
        queryFn: () => getActivityCancellationPolicies(activityId),
        enabled: !!activityId,
    });

    useEffect(() => {
        hasHydratedCancellationFromApi.current = false;
    }, [activityId]);

    useEffect(() => {
        // Prefer API payload when activityId exists
        if (activityId) {
            if (isFetching) return;

            const list = Array.isArray(cancellationData?.data) ? cancellationData.data : [];

            const applyListToState = (rows) => {
                const asStrings = rows
                    .map((p) => String(p?.description || "").trim())
                    .filter(Boolean);
                setPolicies(asStrings);
                onValidityChange?.(true);
            };

            // First settled response for this activity: mirror server (empty or not).
            if (!hasHydratedCancellationFromApi.current) {
                hasHydratedCancellationFromApi.current = true;
                if (list.length) {
                    applyListToState(list);
                } else {
                    setPolicies([]);
                    onValidityChange?.(true);
                }
                return;
            }

            // After hydration: only replace local state when server has rows (e.g. post-save invalidate).
            // Do not call setPolicies([]) here — that was clearing "Add" drafts whenever the query refetched empty.
            if (list.length > 0) {
                applyListToState(list);
            }
            return;
        }

        // Hydrate from localStorage only if parent didn't pass anything yet.
        if (Array.isArray(policies) && policies.length > 0) return;
        try {
            const raw = localStorage.getItem(storageKey);
            if (raw) {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    setPolicies(parsed.filter((p) => typeof p === "string" && p.trim()));
                }
            }
        } catch {
            // ignore malformed storage
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storageKey, activityId, cancellationData, isFetching]);

    useEffect(() => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(policies));
        } catch {
            // ignore storage failures
        }
    }, [policies, storageKey]);

    useEffect(() => {
        onValidityChange?.(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleAdd = () => {
        if (!text.trim()) return;
        preserveWindowScroll(() => {
            setPolicies((prev) => [...prev, text.trim()]);
            setText("");
        });
    };

    const saveMutation = useMutation({
        mutationFn: async (nextPolicies) => {
            if (!activityId) {
                return { status: true };
            }

            const rows = (nextPolicies || []).filter(
                (desc) => typeof desc === "string" && desc.trim()
            );

            const payload = {
                activity_id: activityId,
                data: rows.map((desc, idx) => ({
                    policy_name: "Cancellation Policy",
                    description: desc.trim(),
                    is_active: true,
                    sort_order: idx,
                    refund_percentage: 0,
                })),
            };

            const hasExisting =
                Array.isArray(cancellationData?.data) && cancellationData.data.length > 0;

            if (payload.data.length === 0) {
                if (hasExisting) {
                    return deleteActivityCancellationPolicies(activityId);
                }
                // Optional section — no policies to persist
                return { status: true };
            }

            if (hasExisting) {
                return updateActivityCancellationPolicies(activityId, payload);
            }

            return createActivityCancellationPolicies(payload);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["activityCancellationPolicies", activityId]);
            setShowSuccess(true);
            setToastMsg("Cancellation policy saved successfully");
        },
        onError: (e) => {
            setShowError(true);
            const apiMessage =
                e?.response?.data?.message ||
                e?.response?.data?.errors?.data?.[0] ||
                e?.message;
            setToastMsg(apiMessage || "Failed to save cancellation policy");
        },
    });

    const handleSave = async () => {
        try {
            await saveMutation.mutateAsync(policies);
        } catch {
            // Let parent stop navigation
            throw new Error("CANCELLATION_SAVE_FAILED");
        }
    };

    useEffect(() => {
        registerSave?.(() => handleSave());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [registerSave, activityId, policies]);

    const confirmDelete = () => {
        if (deleteModal.index === null) return;
        preserveWindowScroll(() => {
            const updatedPolicies = policies.filter((_, i) => i !== deleteModal.index);
            setPolicies(updatedPolicies);
            setDeleteModal({ isOpen: false, index: null });
        });
    };

    return (
        <div className="w-full space-y-4">
            {showError && toastMsg && (
                <Toster message={toastMsg} type="error" onClose={() => setShowError(false)} />
            )}
            {showSuccess && toastMsg && (
                <Toster message={toastMsg} type="success" onClose={() => setShowSuccess(false)} />
            )}
            <div className={`${abEmptyState} space-y-6`} data-prevent-section-scroll="true">
                <div className="space-y-2">
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Add new policy rule</label>
                    <div className="flex gap-3">
                        <input
                            type="text"
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="e.g., Full refund if cancelled 48 hours in advance..."
                            className={abListFieldInput}
                            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                        />
                        <button
                            type="button"
                            onClick={handleAdd}
                            disabled={!text.trim()}
                            className={`${abAddBtn} shrink-0 disabled:cursor-not-allowed disabled:opacity-50`}
                        >
                            Add
                        </button>
                    </div>
                </div>

                {/* List Section */}
                <div className="space-y-3">
                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Current cancellation policies</label>
                    <div className="space-y-3">
                        {activityId && isFetching ? (
                            <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 py-10 flex flex-col items-center justify-center text-gray-400">
                                <p className="text-xs italic font-medium">Loading cancellation policies…</p>
                            </div>
                        ) : (
                            policies.length === 0 ? (
                                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 py-10 flex flex-col items-center justify-center text-gray-400">
                                    <i className="fi fi-rr-document text-3xl mb-2 opacity-20"></i>
                                    <p className="text-xs italic font-medium">No policies added yet. Add one above.</p>
                                </div>
                            ) : (
                                policies.map((policy, index) => (
                                    <div
                                        key={index}
                                        className="w-full flex justify-between items-center bg-white hover:bg-gray-50 px-4 py-2 rounded-lg border border-gray-100 hover:border-gray-200 transition-all group shadow-sm"
                                    >
                                        <div className="flex items-start space-x-4 flex-1 pr-4">
                                            <div className="w-2 h-2 rounded-full bg-primary/40 mt-1.5 group-hover:bg-primary transition-colors shrink-0"></div>
                                            <span className="text-sm text-gray-700 font-semibold leading-relaxed">{policy}</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setDeleteModal({ isOpen: true, index: index })}
                                            className={abDeleteBtn}
                                        >
                                            <i className="fi fi-rr-trash text-xs"></i>
                                        </button>
                                    </div>
                                ))
                            )
                        )}
                    </div>
                </div>
            </div>

            {/* Note Section */}
            <div className="bg-amber-50/50 border border-amber-100 p-4 rounded-xl flex gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600 shrink-0 shadow-sm">
                    <i className="fi fi-rr-info"></i>
                </div>
                <div className="space-y-1">
                    <h4 className="text-sm font-bold text-amber-900">Important Note</h4>
                    <p className="text-xs text-amber-800/70 leading-relaxed font-semibold">
                        Cancellation policies are visible to the customer during checkout. Please be clear and concise to avoid disputes.
                    </p>
                </div>
            </div>

            <DeleteConfirmationModal 
                isOpen={deleteModal.isOpen}
                onCancel={() => setDeleteModal({ isOpen: false, index: null })}
                onConfirm={confirmDelete}
                title="Delete Policy"
                message="Are you sure you want to delete this cancellation policy? This action cannot be undone."
            />
        </div>
    );
};

export default ActivityCancellation;
